/**
 * EditMainModal — правка «Основного» клиента через PATCH /admin/clients/:id.
 * Поля различаются по типу (клиника: название/адрес/город/язык/телефон
 * бизнеса; соло: ФИО/специализация/город/язык/адрес приёма). Уезжает дифф.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {
  type ClientDetail,
  type ClientListItem,
  updateClient,
  type UpdateClientInput,
} from '@services/clients';
import {keys} from '@queries/keys';
import {useCityOptions, useSpecializationOptions} from '@hooks/useFilterRefs';
import {Field, TextInput} from '@components/FormKit';
import {cn} from '@lib/utils';
import {formatKgPhone, phoneDigits} from '@utils/phone';

import {ModalShell} from '../ModalShell';

const LANGS = ['Русский', 'Кыргызча'];

type Props = {
  open: boolean;
  client: ClientListItem;
  detail: ClientDetail | null;
  onClose: () => void;
  onSaved: () => void;
};

export const EditMainModal = ({open, client, detail, onClose, onSaved}: Props) => {
  const queryClient = useQueryClient();
  const isClinic = client.type === 'clinic';
  const cityOptions = useCityOptions();
  const specializationOptions = useSpecializationOptions();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [language, setLanguage] = useState('Русский');
  const [businessPhone, setBusinessPhone] = useState('');
  const [specialization, setSpecialization] = useState('');

  /** Сброс полей на каждое открытие — из карточки */
  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setName(client.name);
      setAddress(detail?.address ?? '');
      setCity(client.cityName === '—' ? '' : client.cityName);
      setLanguage(detail?.language ?? 'Русский');
      setBusinessPhone(phoneDigits(detail?.businessPhone ?? ''));
      setSpecialization(detail?.specialization ?? '');
    }
  }

  const mutation = useMutation({
    mutationFn: updateClient,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      onSaved();
    },
  });

  const handleSubmit = () => {
    // Дифф: PATCH принимает только изменяемые поля
    const patch: UpdateClientInput = {id: client.id};
    if (name.trim() !== client.name && name.trim().length >= 2) patch.name = name.trim();
    if (address.trim() !== (detail?.address ?? '')) patch.address = address.trim();
    if (city !== '' && city !== client.cityName) patch.city = city;
    if (language !== (detail?.language ?? 'Русский')) patch.language = language;
    if (businessPhone !== phoneDigits(detail?.businessPhone ?? '')) {
      patch.businessPhone = businessPhone;
    }
    if (!isClinic && specialization !== '' && specialization !== (detail?.specialization ?? '')) {
      patch.specialization = specialization;
    }
    if (Object.keys(patch).length === 1) {
      onClose();
      return;
    }
    mutation.mutate(patch);
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Основные данные"
      submitLabel="Сохранить"
      onSubmit={handleSubmit}
      pending={mutation.isPending}
      submitDisabled={name.trim().length < 2}
      error={mutation.isError ? 'Не удалось сохранить. Попробуйте ещё раз' : null}
      width={440}
    >
      <Field label={isClinic ? 'Название клиники' : 'ФИО'}>
        <TextInput value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      {!isClinic && (
        <Field label="Специализация">
          <RefSelect
            value={specialization}
            onChange={setSpecialization}
            options={specializationOptions}
          />
        </Field>
      )}
      <Field label={isClinic ? 'Адрес' : 'Адрес приёма'}>
        <TextInput
          placeholder="Улица, дом, офис"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
      </Field>
      <div className="flex gap-[11px]">
        <Field label="Город" className="flex-1">
          <RefSelect value={city} onChange={setCity} options={cityOptions} />
        </Field>
        <Field label="Язык" className="flex-1">
          <RefSelect value={language} onChange={setLanguage} options={LANGS} />
        </Field>
      </div>
      {isClinic && (
        <Field label="Телефон бизнеса">
          <TextInput
            mono
            placeholder="700 000 000"
            value={formatKgPhone(businessPhone)}
            onChange={(e) => setBusinessPhone(phoneDigits(e.target.value))}
          />
        </Field>
      )}
    </ModalShell>
  );
};

/** Нативный селект в стиле TextInput (справочники) */
const RefSelect = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={cn(
      'h-11 w-full appearance-none rounded-[10px] border border-input bg-card px-[13px] text-sm outline-none transition',
      'focus:border-primary focus:ring-[3px] focus:ring-primary/12',
      value === '' && 'text-muted-foreground',
    )}
  >
    <option value="" disabled>
      Выберите
    </option>
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);
